import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios'
import { env } from 'lib/env'
import { hasFile, isObject } from 'utils/checkers'

import { getAuthToken } from '@/lib/auth'
import { getLocale } from '@/lib/i18n/request'

interface FetcherOptions
  extends Omit<
    AxiosRequestConfig,
    'auth' | 'baseURL' | 'bearerToken' | 'data' | 'url'
  > {
  auth?: boolean
  baseUrl?: string
  bearerToken?: boolean
  body?: unknown
  enabled?: boolean
  method?: Method
  version?: number
}

/**
 * Promise-based HTTP client around Axios with strong typing and sensible defaults.
 *
 * - Path is typed via `ApiPath` for IntelliSense and safety.
 * - Automatically sets `Accept-Language` using runtime-aware locale detection.
 * - Adds `Authorization: Bearer <token>` when `auth: true`.
 * - If body includes any `File`, converts payload to `FormData` and appends `_method: "POST"`.
 * - Returns `T` based on the caller-provided generic.
 * - Throws typed `AxiosError<API<unknown>>` with human-readable `message`.
 *
 * @template T Response data type
 * @param path ApiPath-compatible endpoint (string literal or composed)
 * @param options Axios options plus `auth`, `enabled`, and `baseUrl` overrides
 * @returns Parsed response data of type T
 */
export async function fetcher<T>(
  path: ENDPOINTS,
  options?: FetcherOptions
): Promise<API<T>>

export async function fetcher<T>(
  path: `${ENDPOINTS}${string}`,
  options?: FetcherOptions
): Promise<API<T>>
export async function fetcher<T>(
  path: (() => string) | string,
  options?: FetcherOptions
): Promise<API<T>>
export async function fetcher<T>(
  path: (() => string) | `${ENDPOINTS}${string}` | ENDPOINTS | string,
  options?: FetcherOptions
): Promise<API<T>> {
  const {
    auth = false,
    baseUrl,
    body: requestBody,
    enabled = true,
    method = 'GET',
    version = 1,
    ...axiosOptions
  } = options || {}

  if (!enabled)
    return {
      statusCode: 0,
      success: false
    }

  // Prioritize `baseUrl` from options, then the default API URL. We avoid carrying an unused baseURL field.
  const effectiveBaseUrl = baseUrl || `${env.NEXT_PUBLIC_API_URL}`

  let sessionToken

  if (auth) {
    sessionToken = await getAuthToken()
  }

  if (!sessionToken && auth && enabled) {
    return {
      statusCode: 0,
      success: false
    }
  }

  const normalizedBaseUrl = effectiveBaseUrl.endsWith('/')
    ? effectiveBaseUrl.slice(0, -1)
    : effectiveBaseUrl
  const resolvedPath = typeof path === 'function' ? path() : path
  const normalizedPath = resolvedPath.startsWith('/')
    ? resolvedPath
    : `/${resolvedPath}`

  const url = `${normalizedBaseUrl}/v${version}${normalizedPath}`
  const bodyContainsFile = hasFile(requestBody)

  // Check if URL already has _method query param (Laravel-style method spoofing)
  const hasMethodInUrl = url.includes('_method=')

  const body = bodyContainsFile
    ? axios.toFormData({
        ...(requestBody as Record<string, unknown>),
        // Only add _method to formdata if not already in URL query params
        ...(!hasMethodInUrl && { _method: 'POST' })
      })
    : requestBody

  const restOfAxiosOptions: AxiosRequestConfig = { ...axiosOptions }

  delete (restOfAxiosOptions as { baseURL?: string }).baseURL

  // Get locale for Accept-Language header
  const locale = await getLocale()

  const config: AxiosRequestConfig = {
    ...restOfAxiosOptions,

    data: body,
    headers: {
      ...restOfAxiosOptions.headers,
      'Accept-Language':
        (restOfAxiosOptions.headers as Record<string, string>)?.[
          'Accept-Language'
        ] ?? locale,
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(auth && { Authorization: `Bearer ${sessionToken}` })
    },
    method,
    url
  }

  try {
    const response = await axios.request<API<T>>(config)
    if (env.NEXT_PUBLIC_DEV_MODE) {
      console.info(
        '%c[FETCHING]%c ' + config.method,
        'background: green; color: white; padding: 2px 4px; border-radius: 2px;',
        'background: transparent; color: inherit;',
        config,
        { response: response.data }
      )
    }
    return response.data
  } catch (error) {
    const axiosError = error as AxiosError<API<unknown>>

    if (env.NEXT_PUBLIC_DEV_MODE) {
      console.error(
        '%c[FETCHING ERROR]%c ' + config.method,
        'background: red; color: white; padding: 2px 4px; border-radius: 2px;',
        'background: transparent; color: inherit;',
        config,
        {
          response: axiosError.response?.data,
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText
        }
      )
    }

    const texts = {
      aborted: 'Request aborted',
      canceled: 'Request canceled',
      default: 'An error occurred while fetching data',
      noResponse: 'No response received from the server'
    }

    let message = ''

    if (axiosError.response) {
      const responseData = axiosError.response.data as API<unknown> | undefined
      if (responseData && typeof responseData === 'object') {
        const serverMessage =
          typeof responseData.message === 'string'
            ? responseData.message.trim()
            : ''

        if (serverMessage) {
          message = serverMessage
        } else if (responseData.errors) {
          const { errors } = responseData
          if (Array.isArray(errors) && errors.length > 0) {
            message = errors.filter((e) => typeof e === 'string').join(', ')
          } else if (typeof errors === 'string') {
            message = errors as string
          } else if (isObject(errors)) {
            const collected: string[] = []
            Object.values(errors).forEach((val) => {
              if (Array.isArray(val)) {
                collected.push(...val.filter((v) => typeof v === 'string'))
              } else if (typeof val === 'string') {
                collected.push(val)
              }
            })
            if (collected.length > 0) {
              message = collected.join(', ')
            }
          }
        }
      }
    }

    if (!message || !message.trim()) {
      if (axiosError.request && !axiosError.response) {
        message = texts.noResponse
      } else if (axiosError.name === 'AbortError') {
        message = texts.aborted
      } else if (axiosError.name === 'CanceledError') {
        message = texts.canceled
      } else {
        message = texts.default
      }
    }

    axiosError.message = message
    console.error(
      'Fetcher error:',
      axiosError.message,
      axiosError.response?.data.errors
        ? `errors: ${JSON.stringify(axiosError.response?.data.errors)}`
        : `errors: undefined`
    )
    throw axiosError
  }
}
